import { TemplateRef } from '@angular/core';
import {
  CellTemplateContext,
  ColumnBuilder,
  DatasetTableConfig,
  DatasetTableBreakpoint,
  TableConfigBuilder,
  textColumn,
  dateColumn,
  editAction,
  deleteAction,
  createCheckboxConfig,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { VehicleModels } from '@models/vehicle.model';
import moment from 'moment-timezone';

export type VehicleCellContext = CellTemplateContext<VehicleModels.Vehicle>;

export interface CreateVehiclesTableConfigDependencies {
  imageCellTemplate: TemplateRef<VehicleCellContext>;
  licensePlateCellTemplate: TemplateRef<VehicleCellContext>;
  ownerCellTemplate: TemplateRef<VehicleCellContext>;
  makeCellTemplate: TemplateRef<VehicleCellContext>;
  colorCellTemplate: TemplateRef<VehicleCellContext>;
  getLocationName: (row: VehicleModels.Vehicle) => string;
  canEdit?: () => boolean;
  checkboxEnabled?: boolean;
  actionsEnabled?: boolean;
  isRowDisabled?: (row: VehicleModels.Vehicle) => boolean;
  deleteTooltip?: string;
}

export function createVehiclesTableConfig(
  dependencies: CreateVehiclesTableConfigDependencies,
): DatasetTableConfig<VehicleModels.Vehicle> {
  const builder = new ColumnBuilder<VehicleModels.Vehicle>();
  const canEditRow = (): boolean => dependencies.canEdit?.() ?? true;

  const tableBuilder = new TableConfigBuilder<VehicleModels.Vehicle>()
    .columns([
      builder
        .column('id')
        .label('')
        .detailsModalLabel('VEHICLES.TABLE.IMAGE')
        .width('minmax(80px, 0.8fr)')
        .cellTemplate(dependencies.imageCellTemplate)
        .build(),

      builder
        .column('plate')
        .label('VEHICLES.TABLE.LICENCE_PLATE')
        .width('minmax(80px, 1fr)')
        .cellTemplate(dependencies.licensePlateCellTemplate)
        .mobileVisible()
        .cardBoldValue()
        .build(),

      builder
        .column('owner')
        .label('VEHICLES.TABLE.OWNER')
        .width('minmax(80px, 1fr)')
        .cellTemplate(dependencies.ownerCellTemplate)
        .build(),

      builder
        .column('make')
        .label('VEHICLES.TABLE.MAKE')
        .width('minmax(80px, 1fr)')
        .cellTemplate(dependencies.makeCellTemplate)
        .build(),

      builder
        .column('color')
        .label('VEHICLES.TABLE.COLOR')
        .width('minmax(80px, 1fr)')
        .cellTemplate(dependencies.colorCellTemplate)
        .build(),

      dateColumn<VehicleModels.Vehicle>('lastSeen', 'VEHICLES.TABLE.LAST_SEEN', {
        width: 'minmax(100px, 1.2fr)',
        mobileVisible: true,
        valueExtractor: (row) => row.lastSeen || null,
        customFormat: 'YYYY MMM D, h:mm A',
        timezone: moment.tz.guess(),
        showTimeZone: true,
        emptyValue: ' — ',
      }),

      builder
        .column('edgeId')
        .label('VEHICLES.TABLE.LOCATION')
        .width('minmax(80px, 1fr)')
        .formatter((_, row) => dependencies.getLocationName(row))
        .mobileVisible()
        .cardIcon('ui-icon-location')
        .build(),
    ]);

  if (dependencies.checkboxEnabled !== false) {
    tableBuilder.checkboxConfig(
      createCheckboxConfig({
        getRowId: (vehicle) => vehicle.plate,
        isRowDisabled: dependencies.isRowDisabled,
      }),
    );
  }

  if (dependencies.actionsEnabled !== false) {
    tableBuilder.actions([
      editAction<VehicleModels.Vehicle>({
        tooltip: 'VEHICLES.TOOLTIPS.EDIT',
        visible: () => canEditRow(),
      }),
      deleteAction<VehicleModels.Vehicle>({
        tooltip: dependencies.deleteTooltip ?? 'VEHICLES.TOOLTIPS.REMOVE',
        visible: () => canEditRow(),
      }),
    ]);
  }

  return tableBuilder
    .extend({
      responsive: {
        breakpoints: {
          mobile: 1200,
          card: 570,
        },
      }
    })
    .trackBy('plate')
    .build();
}
